import { useNavigate } from "react-router-dom";
import { Star, BadgeCheck, Sparkles, Rocket, MapPin, Globe, Shuffle } from "lucide-react";
import { MOCK_SERVICES } from "@/lib/mockData";
import { useAuth } from "@/context/AuthContext";
import { isBoostActive } from "@/lib/monetizationStore";
import { formatServicePrice } from "@/lib/entitiesStore";
import { useMoneVersion } from "@/lib/hooks";
import MoreMenu from "@/components/MoreMenu";

const fmt = (n, c) => `${c}${n.toLocaleString()}`;

const SERVICE_TYPE_META = {
    in_person: { label: "In-person", Icon: MapPin },
    online:    { label: "Online",    Icon: Globe },
    hybrid:    { label: "Hybrid",    Icon: Shuffle },
};

/**
 * Service card — elevated, with verified badge and premium glow border when applicable.
 */
const ServiceCard = ({ service, onClick, testId }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const v = useMoneVersion();
    const boosted = user ? isBoostActive(user.id, "service", service.id) : false;
    void v;
    const handle = onClick || (() => navigate(`/services/${service.id}`));
    const isOwner = !!(user && service?.ownerId && user.id === service.ownerId);
    return (
        <div
            data-testid={testId || `service-card-${service.id}`}
            onClick={handle}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handle(); }}
            role="button"
            tabIndex={0}
            className={[
                "relative w-full text-left rounded-2xl overflow-hidden border bg-card cursor-pointer",
                "transition-[transform,box-shadow] duration-200 active:scale-[0.98]",
                service.premium
                    ? "border-primary/50 shadow-[0_0_0_1px_hsl(var(--primary)/0.35),0_14px_32px_-14px_hsl(var(--primary)/0.45)]"
                    : "border-border shadow-card",
            ].join(" ")}
        >
            <div className="relative aspect-[4/3] bg-muted">
                {service.image ? (
                    <img
                        src={service.image}
                        alt={service.name}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                ) : null}
                {boosted && (
                    <span
                        data-testid={`service-boosted-${service.id}`}
                        className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold text-primary-foreground shadow-card"
                        style={{
                            background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                        }}
                    >
                        <Rocket size={10} strokeWidth={2.6} />
                        Boosted
                    </span>
                )}
                {service.premium && (
                    <span
                        className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10.5px] font-semibold text-primary-foreground shadow-card"
                        style={{
                            background:
                                "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.78) 100%)",
                        }}
                    >
                        <Sparkles size={11} strokeWidth={2.6} />
                        Premium
                    </span>
                )}
                {SERVICE_TYPE_META[service.serviceType] && (() => {
                    const { label, Icon } = SERVICE_TYPE_META[service.serviceType];
                    return (
                        <span
                            data-testid={`service-type-${service.id}`}
                            className="absolute bottom-2.5 left-2.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-background/90 text-foreground border border-border shadow-soft backdrop-blur-md"
                        >
                            <Icon size={10} strokeWidth={2.6} />
                            {label}
                        </span>
                    );
                })()}
            </div>
            <div className="p-3">
                <div className="flex items-center gap-1.5">
                    <span className="font-display text-[14px] font-semibold leading-tight line-clamp-1">
                        {service.name}
                    </span>
                    {service.verified && (
                        <BadgeCheck
                            size={14}
                            strokeWidth={2.6}
                            className="text-primary shrink-0"
                            aria-label="Verified"
                        />
                    )}
                </div>
                <div className="mt-1 flex items-center justify-between text-[12px] text-muted-foreground">
                    <span className="truncate">{service.category}</span>
                    <span className="inline-flex items-center gap-0.5 text-foreground font-medium shrink-0">
                        <Star size={11} strokeWidth={2.6} className="fill-amber-400 text-amber-400" />
                        {service.rating}
                    </span>
                </div>
                <div className="mt-0.5 text-[12px] text-foreground font-medium truncate">
                    {formatServicePrice(service)}
                </div>
            </div>
            {!isOwner && (
                <div
                    className="absolute top-2 right-2"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                >
                    <MoreMenu
                        target={{ kind: "service", id: service.id, name: service.name }}
                        testId={`service-card-more-${service.id}`}
                        position="right"
                        className="h-8 w-8 rounded-full bg-background/90 border border-border/80 shadow-soft hover:bg-background"
                    />
                </div>
            )}
        </div>
    );
};

/**
 * Horizontal scroller for popular services.
 */
export const PopularServicesRow = ({ services = MOCK_SERVICES }) => {
    return (
        <div
            className="flex gap-3 px-5 overflow-x-auto no-scrollbar snap-x snap-mandatory"
            data-testid="popular-services-row"
        >
            {services.map((s) => (
                <div key={s.id} className="shrink-0 w-44 snap-start">
                    <ServiceCard service={s} />
                </div>
            ))}
        </div>
    );
};

export { ServiceCard, fmt };
